import { useState } from "react";
import { 
  Calendar, 
  Plus, 
  FileText, 
  ChevronRight, 
  Loader2, 
  BarChart4,
  ChevronDown,
  Target,
  Award,
  TrendingUp
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useAccountPlanning } from "@/hooks/useAccountPlanning";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { AccountPlanForm } from "@/components/account/AccountPlanForm";
import { Skeleton } from "@/components/ui/skeleton";
import { AccountPlan } from "@/types/growth.types";

interface AccountPlanningTabProps {
  organizationId: string;
  organizationName: string;
}

export function AccountPlanningTab({ 
  organizationId,
  organizationName
}: AccountPlanningTabProps) {
  const navigate = useNavigate();
  const [showNewPlanDialog, setShowNewPlanDialog] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState("current");
  
  // Get current year and quarter
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
  
  // Fetch account plans for this organization
  const { 
    getOrganizationAccountPlans,
    getAccountPlan,
    stats
  } = useAccountPlanning();
  
  const { 
    data: organizationPlans,
    isLoading: isLoadingPlans 
  } = getOrganizationAccountPlans(organizationId);
  
  const { 
    data: selectedPlan,
    isLoading: isLoadingSelectedPlan 
  } = getAccountPlan(selectedPlanId || "");
  
  // Filter plans by current, past, and future
  const currentPlans = organizationPlans?.filter(plan => 
    plan.plan_year === currentYear && plan.plan_quarter === currentQuarter
  ) || [];
  
  const pastPlans = organizationPlans?.filter(plan =>
    (plan.plan_year < currentYear) || 
    (plan.plan_year === currentYear && plan.plan_quarter < currentQuarter)
  ).sort((a, b) => 
    b.plan_year - a.plan_year || b.plan_quarter - a.plan_quarter
  ) || [];
  
  const futurePlans = organizationPlans?.filter(plan =>
    (plan.plan_year > currentYear) || 
    (plan.plan_year === currentYear && plan.plan_quarter > currentQuarter)
  ).sort((a, b) => 
    a.plan_year - b.plan_year || a.plan_quarter - b.plan_quarter
  ) || [];
  
  // Format the quarter and year
  const formatQuarterYear = (plan: AccountPlan) => {
    return `Q${plan.plan_quarter} ${plan.plan_year}`;
  };
  
  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "draft": return "outline";
      case "in_review": return "secondary";
      case "approved": return "default";
      case "in_progress": return "blue";
      case "completed": return "green";
      default: return "outline";
    }
  };
  
  // Get priority badge variant
  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "yellow";
      case "low": return "gray";
      default: return "outline";
    }
  };
  
  // Calculate plan progress
  const calculatePlanProgress = (plan: AccountPlan) => {
    switch (plan.status) {
      case "draft": return 10;
      case "in_review": return 30;
      case "approved": return 50;
      case "in_progress": return 75;
      case "completed": return 100;
      default: return 0;
    }
  };

  // Handle plan click
  const handlePlanClick = (planId: string) => {
    setSelectedPlanId(planId);
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setShowNewPlanDialog(false);
    setSelectedPlanId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">Account Planning</h2>
        <Button onClick={() => setShowNewPlanDialog(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Account Plan
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Plan Overview / KPI Cards */}
        <Card className="col-span-1 lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Planning Overview
            </CardTitle>
            <CardDescription>
              Key metrics and planning status for {organizationName}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Current Quarter Plans
                      </p>
                      <h3 className="text-2xl font-bold">
                        {isLoadingPlans ? (
                          <Skeleton className="h-8 w-16" />
                        ) : (
                          currentPlans.length
                        )}
                      </h3>
                    </div>
                    <Calendar className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Q{currentQuarter} {currentYear}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Revenue Target
                      </p>
                      <h3 className="text-2xl font-bold">
                        {isLoadingPlans || currentPlans.length === 0 ? (
                          <Skeleton className="h-8 w-24" />
                        ) : (
                          `$${currentPlans[0]?.revenue_target?.toLocaleString() || "0"}`
                        )}
                      </h3>
                    </div>
                    <TrendingUp className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {currentPlans.length > 0 && currentPlans[0]?.growth_target_percentage ? 
                      `${currentPlans[0].growth_target_percentage}% growth target` : 
                      'No growth target set'}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Upcoming Plans
                      </p>
                      <h3 className="text-2xl font-bold">
                        {isLoadingPlans ? (
                          <Skeleton className="h-8 w-16" />
                        ) : (
                          futurePlans.length
                        )}
                      </h3>
                    </div>
                    <BarChart4 className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {futurePlans.length > 0 ? 
                      `Next up: Q${futurePlans[0]?.plan_quarter} ${futurePlans[0]?.plan_year}` : 
                      'No future plans'}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">
                        Plan Completion
                      </p>
                      <h3 className="text-2xl font-bold">
                        {isLoadingPlans ? (
                          <Skeleton className="h-8 w-16" />
                        ) : (
                          `${organizationPlans?.filter(p => p.status === "completed").length || 0}/${organizationPlans?.length || 0}`
                        )}
                      </h3>
                    </div>
                    <Award className="h-8 w-8 text-muted-foreground" />
                  </div>
                  {isLoadingPlans ? (
                    <Skeleton className="h-3 w-full mt-2" />
                  ) : (
                    <p className="text-xs text-muted-foreground mt-2">
                      {`${Math.round((organizationPlans?.filter(p => p.status === "completed").length || 0) / (organizationPlans?.length || 1) * 100)}% completion rate`}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>
        
        {/* List of plans */}
        <div className="col-span-1">
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Account Plans
              </CardTitle>
              <Tabs 
                defaultValue="current" 
                value={selectedTab}
                onValueChange={setSelectedTab}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="current">Current</TabsTrigger>
                  <TabsTrigger value="past">Past</TabsTrigger>
                  <TabsTrigger value="future">Future</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingPlans ? (
                <div className="space-y-2 p-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-2 p-2 border rounded-md">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="divide-y">
                  <TabsContent value="current" className="mt-0">
                    {currentPlans.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Calendar className="h-12 w-12 text-muted-foreground mb-3" />
                        <h3 className="text-lg font-medium">No Current Plans</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md">
                          No account plans for the current quarter. Create one to start planning.
                        </p>
                        <Button 
                          onClick={() => setShowNewPlanDialog(true)} 
                          variant="outline" 
                          className="mt-4"
                        >
                          Create Q{currentQuarter} {currentYear} Plan
                        </Button>
                      </div>
                    ) : (
                      currentPlans.map((plan) => (
                        <div
                          key={plan.id}
                          className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                            selectedPlanId === plan.id ? "bg-muted" : ""
                          }`}
                          onClick={() => handlePlanClick(plan.id)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge>Q{plan.plan_quarter} {plan.plan_year}</Badge>
                                <Badge variant={getStatusBadgeVariant(plan.status)}>
                                  {plan.status.replace("_", " ")}
                                </Badge>
                              </div>
                              <h4 className="font-medium mt-1">
                                {plan.strategic_goals ? (
                                  plan.strategic_goals.split("\n")[0]?.substring(0, 50) + "..."
                                ) : (
                                  "Account Plan"
                                )}
                              </h4>
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <Progress value={calculatePlanProgress(plan)} className="h-1" />
                        </div>
                      ))
                    )}
                  </TabsContent>
                  
                  <TabsContent value="past" className="mt-0">
                    {pastPlans.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Calendar className="h-12 w-12 text-muted-foreground mb-3" />
                        <h3 className="text-lg font-medium">No Past Plans</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md">
                          No previous account plans exist for this organization.
                        </p>
                      </div>
                    ) : (
                      pastPlans.map((plan) => (
                        <div
                          key={plan.id}
                          className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                            selectedPlanId === plan.id ? "bg-muted" : ""
                          }`}
                          onClick={() => handlePlanClick(plan.id)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">Q{plan.plan_quarter} {plan.plan_year}</Badge>
                                <Badge variant={getStatusBadgeVariant(plan.status)}>
                                  {plan.status.replace("_", " ")}
                                </Badge>
                              </div>
                              <h4 className="font-medium mt-1 text-muted-foreground">
                                {plan.strategic_goals ? (
                                  plan.strategic_goals.split("\n")[0]?.substring(0, 50) + "..."
                                ) : (
                                  "Account Plan"
                                )}
                              </h4>
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <Progress value={calculatePlanProgress(plan)} className="h-1" />
                        </div>
                      ))
                    )}
                  </TabsContent>
                  
                  <TabsContent value="future" className="mt-0">
                    {futurePlans.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Calendar className="h-12 w-12 text-muted-foreground mb-3" />
                        <h3 className="text-lg font-medium">No Future Plans</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md">
                          No future account plans exist for this organization.
                        </p>
                        <Button 
                          onClick={() => setShowNewPlanDialog(true)} 
                          variant="outline" 
                          className="mt-4"
                        >
                          Create Future Plan
                        </Button>
                      </div>
                    ) : (
                      futurePlans.map((plan) => (
                        <div
                          key={plan.id}
                          className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
                            selectedPlanId === plan.id ? "bg-muted" : ""
                          }`}
                          onClick={() => handlePlanClick(plan.id)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge>Q{plan.plan_quarter} {plan.plan_year}</Badge>
                                <Badge variant={getStatusBadgeVariant(plan.status)}>
                                  {plan.status.replace("_", " ")}
                                </Badge>
                              </div>
                              <h4 className="font-medium mt-1">
                                {plan.strategic_goals ? (
                                  plan.strategic_goals.split("\n")[0]?.substring(0, 50) + "..."
                                ) : (
                                  "Account Plan"
                                )}
                              </h4>
                            </div>
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <Progress value={calculatePlanProgress(plan)} className="h-1" />
                        </div>
                      ))
                    )}
                  </TabsContent>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Plan details */}
        <div className="col-span-1 lg:col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Plan Details
              </CardTitle>
              <CardDescription>
                {selectedPlan ? (
                  `Account plan for ${organizationName} - Q${selectedPlan.plan_quarter} ${selectedPlan.plan_year}`
                ) : (
                  "Select a plan to view details"
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedPlanId ? (
                isLoadingSelectedPlan ? (
                  <div className="space-y-4">
                    <Skeleton className="h-8 w-2/3" />
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-12 w-1/3" />
                  </div>
                ) : selectedPlan ? (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge>{formatQuarterYear(selectedPlan)}</Badge>
                        <Badge variant={getStatusBadgeVariant(selectedPlan.status)}>
                          {selectedPlan.status.replace("_", " ")}
                        </Badge>
                        <Badge variant={getPriorityBadgeVariant(selectedPlan.priority_level)}>
                          {selectedPlan.priority_level} priority
                        </Badge>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setShowNewPlanDialog(true);
                          // Keep the selected plan ID to edit it
                        }}
                      >
                        Edit Plan
                      </Button>
                    </div>
                    
                    <Accordion type="single" collapsible defaultValue="goals">
                      <AccordionItem value="goals">
                        <AccordionTrigger className="text-lg font-semibold">
                          Strategic Goals
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="whitespace-pre-wrap prose prose-sm max-w-none">
                            {selectedPlan.strategic_goals || "No strategic goals defined."}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="whitespace">
                        <AccordionTrigger className="text-lg font-semibold">
                          Whitespace Assessment
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="whitespace-pre-wrap prose prose-sm max-w-none">
                            {selectedPlan.whitespace_assessment || "No whitespace assessment defined."}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="challenges">
                        <AccordionTrigger className="text-lg font-semibold">
                          Challenges & Risks
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="whitespace-pre-wrap prose prose-sm max-w-none">
                            {selectedPlan.challenges || "No challenges defined."}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="opportunities">
                        <AccordionTrigger className="text-lg font-semibold">
                          Opportunities
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="whitespace-pre-wrap prose prose-sm max-w-none">
                            {selectedPlan.opportunities_summary || "No opportunities defined."}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="initiatives">
                        <AccordionTrigger className="text-lg font-semibold">
                          Key Initiatives
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="whitespace-pre-wrap prose prose-sm max-w-none">
                            {selectedPlan.key_initiatives || "No key initiatives defined."}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                      
                      <AccordionItem value="metrics">
                        <AccordionTrigger className="text-lg font-semibold">
                          Success Metrics
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="whitespace-pre-wrap prose prose-sm max-w-none">
                            {selectedPlan.success_metrics || "No success metrics defined."}
                          </div>
                          
                          <div className="mt-4 space-y-3">
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-medium">Revenue Target</span>
                                <span className="text-sm">
                                  ${selectedPlan.revenue_target?.toLocaleString() || "0"}
                                </span>
                              </div>
                              <Progress value={50} className="h-1" />
                            </div>
                            
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-medium">Growth Target</span>
                                <span className="text-sm">
                                  {selectedPlan.growth_target_percentage || 0}%
                                </span>
                              </div>
                              <Progress value={65} className="h-1" />
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    
                    <div className="pt-4">
                      <h3 className="text-md font-semibold mb-2">Plan Timeline</h3>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Event</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell>Plan Created</TableCell>
                            <TableCell>
                              {selectedPlan.created_at ? 
                                format(new Date(selectedPlan.created_at), 'MMM d, yyyy') : 
                                'Unknown'
                              }
                            </TableCell>
                            <TableCell>
                              <Badge variant="green">Completed</Badge>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Plan Approval</TableCell>
                            <TableCell>
                              {selectedPlan.status === 'approved' || 
                               selectedPlan.status === 'in_progress' || 
                               selectedPlan.status === 'completed' ? 
                                format(new Date(), 'MMM d, yyyy') : 
                                'Pending'
                              }
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                selectedPlan.status === 'approved' || 
                                selectedPlan.status === 'in_progress' || 
                                selectedPlan.status === 'completed' ? 
                                  "green" : "yellow"
                              }>
                                {selectedPlan.status === 'approved' || 
                                 selectedPlan.status === 'in_progress' || 
                                 selectedPlan.status === 'completed' ? 
                                  "Completed" : "Pending"
                                }
                              </Badge>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Plan Execution</TableCell>
                            <TableCell>
                              {selectedPlan.status === 'in_progress' || 
                               selectedPlan.status === 'completed' ? 
                                format(new Date(), 'MMM d, yyyy') : 
                                'Not Started'
                              }
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                selectedPlan.status === 'completed' ? 
                                  "green" : 
                                  selectedPlan.status === 'in_progress' ? 
                                    "blue" : "outline"
                              }>
                                {selectedPlan.status === 'completed' ? 
                                  "Completed" : 
                                  selectedPlan.status === 'in_progress' ? 
                                    "In Progress" : "Not Started"
                                }
                              </Badge>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mb-3" />
                    <h3 className="text-lg font-medium">Plan Not Found</h3>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md">
                      The selected plan could not be found or has been deleted.
                    </p>
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mb-3" />
                  <h3 className="text-lg font-medium">No Plan Selected</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md">
                    Select a plan from the list to view its details or create a new plan.
                  </p>
                  <Button 
                    onClick={() => setShowNewPlanDialog(true)} 
                    variant="outline" 
                    className="mt-4"
                  >
                    Create New Plan
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* New Plan Dialog */}
      <Dialog 
        open={showNewPlanDialog} 
        onOpenChange={(open) => {
          if (!open) handleDialogClose();
        }}
      >
        <DialogContent className="max-w-4xl h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedPlanId ? "Edit Account Plan" : "Create New Account Plan"}
            </DialogTitle>
            <DialogDescription>
              {selectedPlanId 
                ? "Update the account plan for this organization" 
                : "Create a new quarterly account plan for this organization"}
            </DialogDescription>
          </DialogHeader>
          
          <AccountPlanForm
            organizationId={organizationId}
            planId={selectedPlanId || undefined}
            onSuccess={handleDialogClose}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}